'use client';

import { useState, useEffect, useCallback } from 'react';
import { slugify } from '@/lib/slug';

export interface Continent {
  id: string;
  name: string;
  name_zh: string;
  name_fr: string;
  name_es: string;
  name_ar: string;
  name_ru: string;
  name_pt: string;
  area_km2: number;
  area_rank: number;
  center: [number, number];
  bbox: [number, number, number, number];
  path: string;
  slug: string;
  countryCount: number; // 包含的国家数量
  color: string; // 用于视觉区分
}

export interface ContinentsStats {
  totalContinents: number;
  totalArea: number;
  largestContinent: string;
  smallestContinent: string;
}

// 大洲颜色配置
const CONTINENT_COLORS = {
  'Asia': '#3B82F6',      // 蓝色
  'Africa': '#10B981',    // 绿色
  'North America': '#F59E0B', // 橙色
  'South America': '#EF4444', // 红色
  'Antarctica': '#8B5CF6',    // 紫色
  'Europe': '#06B6D4',    // 青色
  'Oceania': '#84CC16',   // 黄绿色
};

export function useContinents() {
  const [continents, setContinents] = useState<Continent[]>([]);
  const [stats, setStats] = useState<ContinentsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadContinents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 并行加载大洲和国家数据
      const [regionsResponse, countriesResponse] = await Promise.all([
        fetch('/data/minis/regions.json'),
        fetch('/data/minis/countries-mini.json')
      ]);

      if (!regionsResponse.ok || !countriesResponse.ok) {
        throw new Error('Failed to load data');
      }
      
      const [regionsData, countriesData] = await Promise.all([
        regionsResponse.json(),
        countriesResponse.json()
      ]);
      
      // 过滤出大洲数据
      const continentData = regionsData.filter((region: any) => region.kind === 'continent');
      
      // 计算每个大洲包含的国家数量
      const continentCountryCount: { [key: string]: number } = {};
      countriesData.forEach((country: any) => {
        if (country.continent) {
          continentCountryCount[country.continent] = (continentCountryCount[country.continent] || 0) + 1;
        }
      });

      // 处理大洲数据
      const processedContinents: Continent[] = continentData.map((continent: any) => ({
        id: continent.id,
        name: continent.name,
        name_zh: continent.name_zh || continent.name,
        name_fr: continent.name_fr || continent.name,
        name_es: continent.name_es || continent.name,
        name_ar: continent.name_ar || continent.name,
        name_ru: continent.name_ru || continent.name,
        name_pt: continent.name_pt || continent.name,
        area_km2: continent.area_km2 || 0,
        area_rank: 0, // 稍后计算
        center: continent.center || [0, 0],
        bbox: continent.bbox || [0, 0, 0, 0],
        path: continent.path || `data/regions/${continent.id}.json`,
        slug: slugify(continent.name),
        countryCount: continentCountryCount[continent.name] || 0,
        color: CONTINENT_COLORS[continent.name as keyof typeof CONTINENT_COLORS] || '#6B7280'
      }));

      // 按面积排序并设置排名
      processedContinents.sort((a, b) => b.area_km2 - a.area_km2);
      processedContinents.forEach((continent, index) => {
        continent.area_rank = index + 1;
      });
      
      setContinents(processedContinents);

      // 计算统计信息
      const totalArea = processedContinents.reduce((sum, continent) => sum + continent.area_km2, 0);
      const largestContinent = processedContinents[0]?.name || '';
      const smallestContinent = processedContinents[processedContinents.length - 1]?.name || '';

      setStats({
        totalContinents: processedContinents.length,
        totalArea: Math.round(totalArea),
        largestContinent,
        smallestContinent
      });

    } catch (err) {
      console.error('Error loading continents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load continents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContinents();
  }, [loadContinents]);

  // 搜索大洲
  const searchContinents = useCallback((query: string) => {
    if (!query.trim()) return continents;
    
    const lowercaseQuery = query.toLowerCase();
    return continents.filter(continent => 
      continent.name.toLowerCase().includes(lowercaseQuery) ||
      continent.name_zh.toLowerCase().includes(lowercaseQuery)
    );
  }, [continents]);

  // 排序
  const sortContinents = useCallback((sortBy: 'area' | 'name', order: 'asc' | 'desc' = 'desc') => {
    const sorted = [...continents];
    
    switch (sortBy) {
      case 'area':
        sorted.sort((a, b) => order === 'desc' ? b.area_km2 - a.area_km2 : a.area_km2 - b.area_km2);
        break;
      case 'name':
        sorted.sort((a, b) => order === 'desc' ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name));
        break;
    }
    
    return sorted;
  }, [continents]);

  return {
    continents,
    stats,
    loading,
    error,
    searchContinents,
    sortContinents,
    refetch: loadContinents
  };
}
